import * as THREE from 'three';
import { FXAAShader } from 'three/addons/shaders/FXAAShader.js';

export type InkLook = {
  ink: THREE.ColorRepresentation;
  skyTop: THREE.ColorRepresentation;
  skyBottom: THREE.ColorRepresentation;
  fog: THREE.ColorRepresentation;
  /** View distance where fog starts and where it's at full strength. */
  fogRange: [number, number];
  /** Outlines fade out between these view distances. */
  lineFade: [number, number];
  lineThickness: number;
  saturation: number;
  tint: THREE.ColorRepresentation;
};

const fullscreenVert = /* glsl */ `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

const compositeFrag = /* glsl */ `
  #include <packing>
  uniform sampler2D tColor;
  uniform sampler2D tInfo;
  uniform sampler2D tDepth;
  uniform vec2 uTexel;
  uniform float uThickness;
  uniform float uNear;
  uniform float uFar;
  uniform vec3 uInk;
  uniform vec2 uLineFade;
  uniform vec3 uSkyTop;
  uniform vec3 uSkyBottom;
  uniform vec3 uFogColor;
  uniform vec2 uFog;
  uniform float uSat;
  uniform vec3 uTint;
  uniform float uDepthEdge;
  uniform float uDepthGrazing;
  uniform vec2 uNormalEdge;
  varying vec2 vUv;

  float viewDist(float d) { return -perspectiveDepthToViewZ(d, uNear, uFar); }
  vec3 decodeN(vec2 e) {
    vec2 f = e * 4.0 - 2.0;
    float l = dot(f, f);
    float g = sqrt(max(0.0, 1.0 - l / 4.0));
    return vec3(f * g, 1.0 - l / 2.0);
  }
  vec3 toSRGB(vec3 c) {
    return mix(c * 12.92, 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c));
  }

  void main() {
    vec2 o = uTexel * uThickness;
    vec2 taps[5] = vec2[5](vec2(0.0), vec2(-1.0, 0.0), vec2(1.0, 0.0), vec2(0.0, -1.0), vec2(0.0, 1.0));
    float ids[5]; float zs[5]; vec3 ns[5]; float ms[5];
    vec4 centre = vec4(0.0);
    for (int i = 0; i < 5; i++) {
      vec2 uv = vUv + o * taps[i];
      vec4 c = texture2D(tColor, uv);
      vec4 inf = texture2D(tInfo, uv);
      if (i == 0) centre = c;
      ids[i] = c.a;
      zs[i] = viewDist(texture2D(tDepth, uv).r);
      ns[i] = decodeN(inf.xy);
      ms[i] = inf.z;
    }
    bool isSky = texture2D(tDepth, vUv).r >= 0.99999;

    // Edges where the surface id, the depth or the normal jumps.
    float idEdge = step(0.002, length(vec2(ids[1] - ids[2], ids[3] - ids[4])));
    float zc = zs[0];
    float zJump = length(vec2(zs[1] - zs[2], zs[3] - zs[4])) / max(zc, 0.001);
    float lim = uDepthEdge + (1.0 - clamp(ns[0].z, 0.0, 1.0)) * uDepthGrazing;
    float depthEdge = smoothstep(lim, lim * 1.6, zJump);
    float nJump = length(vec2(distance(ns[1], ns[2]), distance(ns[3], ns[4])));
    float normalEdge = smoothstep(uNormalEdge.x, uNormalEdge.y, nJump);

    // On silhouettes the nearer surface decides whether the line is drawn.
    float nearest = zc; float contribution = ms[0];
    for (int i = 1; i < 5; i++) {
      if (zs[i] < nearest * 0.97) { nearest = zs[i]; contribution = ms[i]; }
    }
    float fade = 1.0 - smoothstep(uLineFade.x, uLineFade.y, nearest);
    float ink = clamp(idEdge + depthEdge + normalEdge, 0.0, 1.0) * contribution * fade;

    vec3 col;
    if (isSky) {
      col = mix(uSkyBottom, uSkyTop, smoothstep(0.0, 1.0, vUv.y));
    } else {
      col = centre.rgb;
      float f = smoothstep(uFog.x, uFog.y, zc);
      col = mix(col, uFogColor, f);
    }
    col = mix(col, uInk, ink);

    float lum = dot(col, vec3(0.2126, 0.7152, 0.0722));
    col = mix(vec3(lum), col, uSat) * uTint;
    gl_FragColor = vec4(toSRGB(clamp(col, 0.0, 1.0)), 1.0);
  }
`;

const linear = (c: THREE.ColorRepresentation) => new THREE.Color(c);

/**
 * Renders the scene once into two colour targets (colour + surface id, and
 * encoded normal + line mask), then draws ink outlines, fog and the grade in a
 * full-screen pass, then FXAA to the screen.
 */
export class InkPipeline {
  readonly gbuffer: THREE.WebGLRenderTarget;
  private readonly graded: THREE.WebGLRenderTarget;
  private readonly composite: THREE.ShaderMaterial;
  private readonly fxaa: THREE.ShaderMaterial;
  private readonly quad: THREE.Mesh;
  private readonly quadScene = new THREE.Scene();
  private readonly quadCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private readonly size = new THREE.Vector2();

  constructor(private readonly renderer: THREE.WebGLRenderer, look: InkLook) {
    this.gbuffer = new THREE.WebGLRenderTarget(1, 1, {
      count: 2,
      type: THREE.HalfFloatType,
      depthBuffer: true,
      depthTexture: new THREE.DepthTexture(1, 1, THREE.FloatType),
    });
    for (const t of this.gbuffer.textures) {
      t.minFilter = THREE.NearestFilter;
      t.magFilter = THREE.NearestFilter;
    }
    this.graded = new THREE.WebGLRenderTarget(1, 1, { type: THREE.UnsignedByteType });

    this.composite = new THREE.ShaderMaterial({
      vertexShader: fullscreenVert,
      fragmentShader: compositeFrag,
      depthTest: false,
      depthWrite: false,
      uniforms: {
        tColor: { value: this.gbuffer.textures[0] },
        tInfo: { value: this.gbuffer.textures[1] },
        tDepth: { value: this.gbuffer.depthTexture },
        uTexel: { value: new THREE.Vector2() },
        uThickness: { value: look.lineThickness },
        uNear: { value: 0.1 },
        uFar: { value: 200 },
        uInk: { value: linear(look.ink) },
        uLineFade: { value: new THREE.Vector2(...look.lineFade) },
        uSkyTop: { value: linear(look.skyTop) },
        uSkyBottom: { value: linear(look.skyBottom) },
        uFogColor: { value: linear(look.fog) },
        uFog: { value: new THREE.Vector2(...look.fogRange) },
        uSat: { value: look.saturation },
        uTint: { value: linear(look.tint) },
        uDepthEdge: { value: 0.06 },
        uDepthGrazing: { value: 0.5 },
        uNormalEdge: { value: new THREE.Vector2(0.55, 0.85) },
      },
    });

    this.fxaa = new THREE.ShaderMaterial({
      ...FXAAShader,
      uniforms: THREE.UniformsUtils.clone(FXAAShader.uniforms),
      depthTest: false,
      depthWrite: false,
    });
    this.fxaa.uniforms.tDiffuse.value = this.graded.texture;

    this.quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.composite);
    this.quad.frustumCulled = false;
    this.quadScene.add(this.quad);
  }

  get uniforms() {
    return this.composite.uniforms;
  }

  setSize(): void {
    this.renderer.getDrawingBufferSize(this.size);
    const { x, y } = this.size;
    this.gbuffer.setSize(x, y);
    this.graded.setSize(x, y);
    this.composite.uniforms.uTexel.value.set(1 / x, 1 / y);
    // Thin lines on small screens, a little heavier on big ones.
    this.composite.uniforms.uThickness.value = Math.max(1, Math.min(2.2, y / 620));
    this.fxaa.uniforms.resolution.value.set(1 / x, 1 / y);
  }

  render(scene: THREE.Scene, camera: THREE.PerspectiveCamera): void {
    const r = this.renderer;
    this.composite.uniforms.uNear.value = camera.near;
    this.composite.uniforms.uFar.value = camera.far;

    r.setRenderTarget(this.gbuffer);
    r.setClearColor(0x000000, 0);
    r.clear(true, true, false);
    r.render(scene, camera);

    this.quad.material = this.composite;
    r.setRenderTarget(this.graded);
    r.render(this.quadScene, this.quadCam);

    this.quad.material = this.fxaa;
    r.setRenderTarget(null);
    r.render(this.quadScene, this.quadCam);
  }

  dispose(): void {
    this.gbuffer.dispose();
    this.graded.dispose();
    this.composite.dispose();
    this.fxaa.dispose();
    this.quad.geometry.dispose();
  }
}
